import { Box, Flex, FlexProps, VStack } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { ReactComponent as EmptyStateIllustration } from 'pages/Conversations/assets/channel-list-empty-state.svg';
import React, { useMemo } from 'react';
import LoadingIndicator from '../LoadingIndicator';

interface ConversationListProps {
  containerRef: React.RefObject<HTMLDivElement>;
  listEndRef: (node?: Element | null | undefined) => void;
  showEmptyState?: boolean;
  hasNextPage?: boolean;
  children: React.ReactNode;
  showUnreadEmptyState?: boolean;
  onViewAllConversationsClick?: () => void;
  containerProps?: FlexProps;
}

export const ConversationsList = ({
  containerRef,
  listEndRef,
  showEmptyState,
  hasNextPage,
  showUnreadEmptyState = false,
  onViewAllConversationsClick,
  containerProps,
  children,
}: ConversationListProps): JSX.Element => {
  const _showEmptyUI = useMemo(
    () => !!(showUnreadEmptyState || showEmptyState),
    [showEmptyState, showUnreadEmptyState],
  );

  return (
    <Flex
      className="ConversationsList__Container--Outer"
      bgColor="background.subtle"
      overflowY="hidden"
      flex="1 0 0"
      {...containerProps}
    >
      <VStack
        className="ConversationsList__Container--Inner"
        align="stretch"
        justify="stretch"
        ref={containerRef}
        bgColor="background.alpha"
        borderTopRadius="md"
        px={3}
        pt={3}
        pb="1px"
        overflowY="auto"
        overflowX="hidden"
        w="100%"
      >
        {children}
        {_showEmptyUI && (
          <VStack align="center" textAlign="center" spacing={5} pt={7}>
            <EmptyStateIllustration />
            <Text variant="subtle">
              {showUnreadEmptyState ? 'There are no more unread conversations.' : 'No conversations found.'}
            </Text>
            {showUnreadEmptyState && (
              <Button variant="ghost" onClick={onViewAllConversationsClick}>
                View all conversations
              </Button>
            )}
          </VStack>
        )}
        <VStack className="Conversations__LoadingContainer" h="100%" w="100%" align="center" justify="center">
          {hasNextPage && !showEmptyState ? (
            <Box className="Conversations__LoadingSpinner" p={5} ref={listEndRef}>
              <LoadingIndicator />
            </Box>
          ) : _showEmptyUI ? null : (
            <>
              {!hasNextPage && (
                <Text className="Conversations__LoadingText" p={5} variant="subtle">
                  You&apos;ve seen them all!
                </Text>
              )}
            </>
          )}
        </VStack>
      </VStack>
    </Flex>
  );
};
